import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Loader2, FileText, X, ShieldCheck, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_COLOR = {
  CRITICAL: "bg-rose-100 text-rose-800 border-rose-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MODERATE: "bg-amber-100 text-amber-800 border-amber-200",
  LOW: "bg-slate-100 text-slate-700 border-slate-200",
};

// ─── Single case row ──────────────────────────────────────────────────────
function CdsCaseRow({ c, onOpen }) {
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-slate-200 bg-white hover:border-slate-300 transition"
      data-testid={`cds-case-row-${c.case_id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Badge variant="outline" className={`shrink-0 text-[10px] font-bold border ${SEV_COLOR[c.expected_min_severity] || SEV_COLOR.LOW}`}>
          {c.expected_min_severity}
        </Badge>
        <Badge variant="outline" className="shrink-0 text-[10px] font-bold border bg-slate-50 text-slate-700 border-slate-200">
          {c.specialty}
        </Badge>
        <span className="truncate text-sm text-slate-800 font-medium">{c.title}</span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onOpen(c)}
        className="shrink-0 border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition"
        data-testid={`cds-view-case-${c.case_id}`}
      >
        <FileText className="w-3.5 h-3.5 mr-1.5" />
        Open Case
      </Button>
    </div>
  );
}

// Reusable field row inside the modal (always renders — "not provided" placeholder).
function FieldRow({ label, value, mono }) {
  const filled = value && String(value).trim().length > 0;
  return (
    <div className="py-2 border-b border-slate-100 last:border-0">
      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`mt-0.5 text-sm ${filled ? "text-slate-900" : "text-slate-400 italic"} ${mono ? "font-mono text-xs" : ""} whitespace-pre-wrap`}>
        {filled ? value : "Not provided"}
      </div>
    </div>
  );
}

// ─── Modal — physician-triage view (S + history + alerts only) ───────────
function CdsCaseDialog({ open, onClose, caseObj }) {
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    if (!open || !caseObj) return;
    let cancelled = false;
    setPhase("running");
    setResult(null);
    setErrMsg(null);
    const payload = {
      ...caseObj.intake,
      communicationMode: "text",
      viewMode: "physician",
      consentGiven: true,
    };
    axios.post(`${API}/intake`, payload, { headers: { "x-system-test": "pai-system-test-2026" } })
      .then(res => { if (!cancelled) { setResult(res.data); setPhase("done"); } })
      .catch(err => { if (!cancelled) { setErrMsg(err.response?.data?.error || err.message || "Pipeline error."); setPhase("error"); } });
    return () => { cancelled = true; };
  }, [open, caseObj]);

  if (!caseObj) return null;
  const intake = caseObj.intake || {};
  const flags = result?.riskFlags || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="cds-case-dialog">
        <DialogHeader className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4">
          <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-700" />
            {caseObj.title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Case ID: {caseObj.case_id} · Specialty: {caseObj.specialty} · Fictional case · No PHI
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-6">
          {/* Demographics + S (Subjective) */}
          <section data-testid="cds-subjective">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Subjective (Patient-Reported)</h3>
            <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-3">
              <div className="grid grid-cols-3 gap-3 pb-2 mb-2 border-b border-slate-200">
                <div><span className="text-[11px] font-bold text-slate-500 uppercase">Age</span><div className="text-sm text-slate-900">{intake.age || "—"}</div></div>
                <div><span className="text-[11px] font-bold text-slate-500 uppercase">Sex</span><div className="text-sm text-slate-900">{intake.sex || "—"}</div></div>
                <div><span className="text-[11px] font-bold text-slate-500 uppercase">Language</span><div className="text-sm text-slate-900">{intake.language || "English"}</div></div>
              </div>
              <FieldRow label="Chief complaint" value={intake.chiefComplaint} />
              <FieldRow label="HPI / symptoms" value={intake.symptoms} />
            </div>
          </section>

          {/* History */}
          <section data-testid="cds-history">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Patient History (Aggregated · Cures Act-Eligible)</h3>
            <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-2">
              <FieldRow label="Past medical history" value={intake.existingConditions} />
              <FieldRow label="Family history" value={intake.familyHistory} />
              <FieldRow label="Allergies" value={intake.allergies} />
              <FieldRow label="Medications" value={intake.medications} mono />
              <FieldRow label="Additional notes (patient-reported)" value={intake.notes} />
            </div>
          </section>

          {/* Risk Alerts — surfaced as alerts, NOT diagnoses */}
          <section data-testid="cds-alerts">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Deterministic Risk Alerts (Engine-Surfaced)</h3>
            {phase === "running" && (
              <div className="flex items-center gap-2 text-sm text-slate-600 py-4 justify-start">
                <Loader2 className="w-4 h-4 animate-spin" /> Running deterministic engines…
              </div>
            )}
            {phase === "error" && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {errMsg}
              </div>
            )}
            {phase === "done" && flags.length === 0 && (
              <div className="text-sm text-slate-500 italic px-4 py-3 border border-slate-200 rounded-md bg-slate-50">
                No engine alerts triggered for this case.
              </div>
            )}
            {phase === "done" && flags.length > 0 && (
              <div className="space-y-2">
                {flags.map((f, i) => (
                  <div key={i} className="rounded-md border border-slate-200 bg-white px-4 py-3" data-testid={`cds-alert-${i}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Badge variant="outline" className={`text-[10px] font-bold border ${SEV_COLOR[f.severity] || SEV_COLOR.LOW}`}>
                        {f.severity}
                      </Badge>
                      <span className="text-sm font-bold text-slate-900">{f.category}</span>
                    </div>
                    {Array.isArray(f.triggers) && f.triggers.length > 0 && (
                      <div className="text-[11px] text-slate-600 mt-1">
                        <span className="font-semibold uppercase tracking-wide text-slate-500">Triggered by:</span>{" "}
                        <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-700">{f.triggers.slice(0, 5).join(" · ")}</code>
                      </div>
                    )}
                    {f.rule_id && (
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        <span className="font-semibold uppercase tracking-wide text-slate-500">Rule:</span> <code className="text-slate-700">{f.rule_id}</code>
                      </div>
                    )}
                    {f.source_guideline && (
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        <span className="font-semibold uppercase tracking-wide text-slate-500">Source:</span> <span className="text-slate-700">{f.source_guideline}</span>
                      </div>
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-slate-500 italic mt-3">
                  Alerts are surfaced for clinical attention only. <strong>Diagnosis, differential, and management remain entirely with the physician.</strong>
                  No LLM-generated assessment, plan, or differential is shown.
                </p>
              </div>
            )}
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="cds-case-close" size="sm">
            <X className="w-3.5 h-3.5 mr-1.5" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function ClinicalCdsPage() {
  const { slug } = useParams();
  const [bank, setBank] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [openCase, setOpenCase] = useState(null);

  useEffect(() => {
    axios.get(`/validator-banks/${slug}.json`)
      .then(res => setBank(res.data))
      .catch(err => setLoadError(`No clinical-CDS case-set found for "${slug}". (${err.message})`));
  }, [slug]);

  const openCdsCase = useCallback((c) => setOpenCase(c), []);
  const closeCdsCase = useCallback(() => setOpenCase(null), []);

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="cds-not-found">
        <div className="max-w-2xl mx-auto rounded-md border border-rose-200 bg-rose-50 px-6 py-5 text-sm text-rose-800">{loadError}</div>
      </div>
    );
  }
  if (!bank) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10 flex items-center justify-center text-slate-600 text-sm" data-testid="cds-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading clinical case set…
      </div>
    );
  }
  const doctor = bank.doctor || {};

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="clinical-cds-page">
      <div className="max-w-4xl mx-auto">
        {/* Top framing — explicit Epic-BPA positioning, no AI-buzzwords */}
        <div className="rounded-lg border-2 border-slate-800 bg-slate-900 text-white p-5 mb-5 shadow-sm" data-testid="cds-framing-card">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-cyan-300 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h1 className="text-lg font-bold tracking-tight">Deterministic Clinical Decision Support</h1>
              <p className="text-sm text-slate-300 mt-1">
                Patient-history aggregation layer · No large language model in the decision loop · Modeled on Epic Best Practice Advisories &amp; Cerner rules.
                Every alert is deterministic — same input → same output, every time, audit-trail per flag. Diagnosis, differential, and management remain entirely with the physician.
              </p>
            </div>
          </div>
        </div>

        {/* Doctor card */}
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/60 p-5 mb-6 shadow-sm" data-testid="cds-doctor-card">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h2 className="text-lg font-bold text-emerald-900">{doctor.name || "Clinical Validator"}</h2>
              <p className="text-sm text-emerald-800 mt-0.5">{doctor.credentials}</p>
              <p className="text-sm text-emerald-700 mt-1">{doctor.affiliation}</p>
              {doctor.experience && <p className="text-xs text-emerald-700 mt-0.5">{doctor.experience}</p>}
              {doctor.email && <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1.5"><Mail className="w-3 h-3" />{doctor.email}</p>}
              {!doctor.email && doctor.email_status && <p className="text-xs text-emerald-700 italic mt-1">{doctor.email_status}</p>}
            </div>
            <Badge variant="outline" className="bg-emerald-600 text-white border-emerald-700 text-[10px] font-bold uppercase tracking-wider shrink-0">
              Clinical Validator
            </Badge>
          </div>
        </div>

        {/* Case set */}
        <div className="mb-4">
          <h2 className="text-base font-bold text-slate-900">Case Set — {bank.cases.length} cases</h2>
          <p className="text-sm text-slate-600 mt-1">
            Each case below shows ONLY: patient subjective history, past medical history, family history, allergies, medications, and the alerts triggered by the deterministic clinical engines.
            No assessment. No plan. No differential. No ICD codes. Approximate review time: 5–10 minutes total.
          </p>
        </div>

        {bank.cases.length === 0 && (
          <div className="rounded-md border-2 border-dashed border-amber-300 bg-amber-50 px-5 py-6 mb-6" data-testid="cds-empty-state">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-base font-bold text-amber-900">Cases pending</h3>
                <p className="text-sm text-amber-800 mt-1">
                  This dedicated case set for {doctor.name || "this validator"} is awaiting case selection.
                  Once {(doctor.name || "the physician").split(" ").slice(0, 2).join(" ")} replies with 3–5 vignettes from her or his clinical experience,
                  they will appear here as a curated review list. The format will match the framing above — Subjective + History + deterministic alerts only,
                  no assessment, no plan, no differential.
                </p>
                <p className="text-xs text-amber-700 italic mt-2">
                  Sample format (for reference):
                  {" "}<a className="underline hover:text-amber-900" href="/clinical-cds/devarajan">/clinical-cds/devarajan</a>
                  {" "}— 18 cases reviewed by Dr. V. N. Devarajan, M.D.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2 mb-6">
          {bank.cases.map(c => <CdsCaseRow key={c.case_id} c={c} onOpen={openCdsCase} />)}
        </div>
      </div>

      <CdsCaseDialog open={!!openCase} onClose={closeCdsCase} caseObj={openCase} />
    </div>
  );
}
